<div class="tw-relative tw-z-[1]">
  <!-- Mode Toggle with Status -->
  <div class="tw-p-3 tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-2">
    <customer-title-content
      class="tw-flex-1"
      [title]="labels.firmwareMode"
      [content]="firmwareModeContent">
    </customer-title-content>
    <mat-slide-toggle
      [checked]="currentMode === 'test'"
      (change)="toggleMode($event.checked ? 'test' : 'production')">
    </mat-slide-toggle>
  </div>

  <!-- Device Type Selector -->
  <div class="tw-w-full tw-flex tw-flex-row tw-justify-end tw-items-center tw-mb-4 tw-relative tw-z-0">
    <app-button-toggle-select
      class="tw-ml-auto"
      [options]="[
        { value: 'INVERTER', label: labels.inverter },
        { value: 'LOGGER', label: labels.logger },
        { value: 'SAFETY', label: labels.safetyConfig }
      ]"
      [value]="selectedDeviceType"
      (valueChange)="selectedDeviceType = $any($event)">
    </app-button-toggle-select>
  </div>

  <!-- Reusable Templates (used in wizards and metadata) -->
  <ng-template #configItem let-label="label" let-value="value">
    <ion-item
      *ngIf="value && value !== ''"
      lines="none">
      <ion-label class="text-grey-400 ion-text-wrap">
        <p class="notes-label">{{ label }}</p>
        <p class="notes-content">{{ value }}</p>
      </ion-label>
    </ion-item>
  </ng-template>

  <!-- Stepper -->
  <mat-stepper
    #stepper
    [linear]="false"
    [orientation]="stepperOrientation"
    [selectedIndex]="selectedStepIndex"
    (selectionChange)="onStepChange($event)">

    <!-- STEP 1: UPLOAD FIRMWARE -->
    <mat-step [label]="labels.uploadFirmware" [editable]="true">
      <div class="tw-py-4">
        <!-- INVERTER UPLOAD -->
        <div *ngIf="selectedDeviceType === 'INVERTER'">
          <customer-title-content
            class="tw-flex-1"
            [title]="labels.uploadInverterTestFirmware">
          </customer-title-content>
          <form [formGroup]="uploadForm">
            <!-- Modern File Upload with Drag & Drop -->
            <zcs-file-upload
              accept=".bin"
              [subtitle]="labels.orClickToBrowse"
              [title]="labels.dragDropBinFile"
              [selectedFile]="selectedFile"
              (fileCleared)="onFileCleared()"
              (fileSelected)="onFileSelected($event)">
            </zcs-file-upload>

            <!-- Extracted Metadata -->
            <div
              *ngIf="extractedMetadata"
              class="tw-mt-4 tw-p-4 tw-bg-grey-100 tw-rounded-lg">
              <h4 class="tw-text-base tw-font-semibold tw-text-grey-800 tw-mb-2">{{ labels.extractedMetadata }}</h4>
              <ion-list
                mode="md"
                lines="none">
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.firmwareName, value: extractedMetadata.firmwareName }"></ng-container>
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.version, value: extractedMetadata.firmwareVersion }"></ng-container>
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.chipModel, value: extractedMetadata.chipModel }"></ng-container>
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.firmwareEffect, value: extractedMetadata.firmwareEffectLabel + ' (' + extractedMetadata.firmwareEffect + ')' }"></ng-container>
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.buildDate, value: extractedMetadata.buildDate }"></ng-container>
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.crc32, value: extractedMetadata.firmwareCRC32 }"></ng-container>
              </ion-list>
            </div>

            <!-- Form Fields -->
            <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mt-4">
              <mat-label class="text-grey-400">{{ labels.deviceModel }} *</mat-label>
              <div class="fx-row fx-gap-3 fx-wrap">
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
                  subscriptSizing="dynamic">
                  <mat-select
                    formControlName="deviceModel"
                    [placeholder]="labels.selectDeviceModel">
                    <mat-option
                      *ngFor="let model of (availableDeviceModels$ | async)"
                      [value]="model.value">
                      {{ model.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>
            </div>

            <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mt-3">
              <mat-label class="text-grey-400">{{ labels.autoFilledFields }}</mat-label>
              <div class="fx-row fx-gap-3 fx-wrap">
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom"
                  subscriptSizing="dynamic">
                  <input
                    formControlName="moduleType"
                    matInput
                    readonly
                    [placeholder]="labels.moduleType">
                </mat-form-field>
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom"
                  subscriptSizing="dynamic">
                  <input
                    formControlName="chipId"
                    matInput
                    readonly
                    [placeholder]="labels.chipId">
                </mat-form-field>
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom"
                  subscriptSizing="dynamic">
                  <input
                    formControlName="version"
                    matInput
                    readonly
                    [placeholder]="labels.versionAutoFilled">
                </mat-form-field>
              </div>
            </div>

            <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mt-3">
              <mat-form-field class="tw-w-full tw-mb-3">
                <mat-label>{{ labels.notes }}</mat-label>
                <textarea
                  formControlName="notes"
                  matInput
                  rows="3"
                  [placeholder]="labels.notesPlaceholder">
                </textarea>
              </mat-form-field>
            </div>

            <!-- Progress Bar -->
            <div
              *ngIf="isUploading"
              class="tw-mt-3">
              <mat-progress-bar
                mode="determinate"
                [value]="uploadProgress">
              </mat-progress-bar>
              <p class="text-grey-400 tw-text-center tw-mt-2">
                {{ labels.uploading }}: {{ uploadProgress }}%
              </p>
            </div>

            <!-- Actions -->
            <div
              class="fx-gap-3 tw-mt-3"
              style="display: flex; flex-direction: row-reverse;">
              <customer-simple-button
                [action]="uploadFirmware"
                [disabled]="!uploadForm.valid || !selectedFile || isUploading"
                [label]="labels.uploadFirmwareButton">
              </customer-simple-button>
              <customer-simple-button
                type="warning"
                [action]="resetUploadForm"
                [disabled]="isUploading"
                [label]="labels.reset">
              </customer-simple-button>
            </div>
          </form>
        </div>

        <!-- LOGGER UPLOAD -->
        <div *ngIf="selectedDeviceType === 'LOGGER'">
          <customer-title-content
            class="tw-flex-1"
            [content]="labels.noMetadataExtractionForLogger"
            [title]="labels.uploadLoggerTestFirmware">
          </customer-title-content>
          <!-- Modern File Upload with Drag & Drop -->
          <zcs-file-upload
            accept=".bin"
            [subtitle]="labels.orClickToBrowse"
            [title]="labels.dragDropLoggerBinFile"
            [selectedFile]="loggerUploadForm.file"
            (fileCleared)="onLoggerFileCleared()"
            (fileSelected)="onLoggerFileSelected($event)">
          </zcs-file-upload>

          <!-- Logger Type -->
          <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mt-4">
            <mat-label class="text-grey-400">{{ labels.loggerType }} *</mat-label>
            <div class="fx-row fx-gap-3 fx-wrap">
              <mat-form-field
                class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
                subscriptSizing="dynamic">
                <mat-select
                  [placeholder]="labels.selectLoggerType"
                  [(ngModel)]="loggerUploadForm.loggerType">
                  <mat-option
                    *ngFor="let type of loggerTypes"
                    [value]="type">
                    {{ type }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <!-- Notes -->
          <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mt-3">
            <mat-label class="text-grey-400">{{ labels.notesOptional }}</mat-label>
            <mat-form-field
              class="custom-mat-form-field custom-border-bottom w-full"
              subscriptSizing="dynamic">
              <textarea
                matInput
                rows="2"
                [placeholder]="labels.anyNotes"
                [(ngModel)]="loggerUploadForm.notes">
              </textarea>
            </mat-form-field>
          </div>

          <!-- Progress Bar -->
          <div
            *ngIf="isUploadingLogger"
            class="tw-mt-3">
            <mat-progress-bar
              mode="determinate"
              [value]="loggerUploadProgress">
            </mat-progress-bar>
            <p class="text-grey-400 tw-text-center tw-mt-2">
              {{ labels.uploading }}: {{ loggerUploadProgress }}%
            </p>
          </div>

          <!-- Actions -->
          <div
            class="fx-gap-3 tw-mt-3"
            style="display: flex; flex-direction: row-reverse;">
            <customer-simple-button
              [action]="uploadLoggerFirmware"
              [disabled]="!loggerUploadForm.file || !loggerUploadForm.loggerType || isUploadingLogger"
              [label]="labels.uploadLoggerFirmware">
            </customer-simple-button>
            <customer-simple-button
              type="warning"
              [action]="resetLoggerUploadForm"
              [disabled]="isUploadingLogger"
              [label]="labels.reset">
            </customer-simple-button>
          </div>
        </div>

        <!-- SAFETY UPLOAD -->
        <div *ngIf="selectedDeviceType === 'SAFETY'">
          <!-- Device Model Selector (required first) -->
          <customer-title-content
            class="tw-flex-1"
            [title]="labels.selectDeviceModel">
          </customer-title-content>
          <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mb-4">
            <mat-label class="text-grey-400">{{ labels.deviceModel }} *</mat-label>
            <mat-form-field
              class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
              subscriptSizing="dynamic">
              <mat-select
                [(ngModel)]="selectedSafetyDeviceModel"
                [placeholder]="labels.selectDeviceModel"
                (selectionChange)="onSafetyDeviceModelChange()">
                <mat-option
                  *ngFor="let model of (availableDeviceModels$ | async)"
                  [value]="model.value">
                  {{ model.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <!-- Two-column layout for Binary and Custom uploads -->
          <div
            *ngIf="selectedSafetyDeviceModel"
            class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">

            <!-- Safety Binary Upload -->
            <div class="tw-border tw-border-grey-200 tw-rounded-lg tw-p-4">
              <customer-title-content
                class="tw-flex-1"
                [content]="labels.safetyBinaryDescription"
                [title]="labels.uploadSafetyBinary">
              </customer-title-content>

              <zcs-file-upload
                accept=".bin"
                [subtitle]="labels.orClickToBrowse"
                [title]="labels.dragDropSafetyBin"
                [selectedFile]="safetyBinaryUploadForm.file"
                (fileCleared)="resetSafetyBinaryUpload()"
                (fileSelected)="onSafetyBinaryFileSelected($event)">
              </zcs-file-upload>

              <!-- Extracted Metadata -->
              <div
                *ngIf="extractedSafetyBinaryMetadata"
                class="tw-mt-4 tw-p-4 tw-bg-grey-100 tw-rounded-lg">
                <h4 class="tw-text-base tw-font-semibold tw-text-grey-800 tw-mb-2">{{ labels.extractedMetadata }}</h4>
                <ion-list mode="md" lines="none">
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.safetyBuildDate, value: extractedSafetyBinaryMetadata.buildDate }"></ng-container>
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.firmwareName, value: extractedSafetyBinaryMetadata.firmwareName }"></ng-container>
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.version, value: extractedSafetyBinaryMetadata.firmwareVersion }"></ng-container>
                </ion-list>
              </div>

              <!-- Notes -->
              <div class="fx-col fx-gap-1 tw-mt-3">
                <mat-label class="text-grey-400">{{ labels.notesOptional }}</mat-label>
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom w-full"
                  subscriptSizing="dynamic">
                  <textarea
                    matInput
                    rows="2"
                    [placeholder]="labels.anyNotes"
                    [(ngModel)]="safetyBinaryUploadForm.notes">
                  </textarea>
                </mat-form-field>
              </div>

              <!-- Progress -->
              <div
                *ngIf="isUploadingSafetyBinary"
                class="tw-mt-3">
                <mat-progress-bar
                  mode="determinate"
                  [value]="safetyBinaryUploadProgress">
                </mat-progress-bar>
                <p class="text-grey-400 tw-text-center tw-mt-2">{{ labels.uploading }}: {{ safetyBinaryUploadProgress }}%</p>
              </div>

              <!-- Actions -->
              <div class="fx-gap-3 tw-mt-3" style="display: flex; flex-direction: row-reverse;">
                <customer-simple-button
                  [action]="uploadSafetyBinary"
                  [disabled]="!safetyBinaryUploadForm.file || !extractedSafetyBinaryMetadata?.buildDate || isUploadingSafetyBinary"
                  [label]="labels.uploadSafetyBinaryButton">
                </customer-simple-button>
                <customer-simple-button
                  type="warning"
                  [action]="resetSafetyBinaryUpload"
                  [disabled]="isUploadingSafetyBinary"
                  [label]="labels.reset">
                </customer-simple-button>
              </div>
            </div>

            <!-- Safety Custom Upload -->
            <div class="tw-border tw-border-grey-200 tw-rounded-lg tw-p-4">
              <customer-title-content
                class="tw-flex-1"
                [content]="labels.safetyCustomDescription"
                [title]="labels.uploadSafetyCustom">
              </customer-title-content>

              <zcs-file-upload
                accept=".txt"
                [subtitle]="labels.orClickToBrowse"
                [title]="labels.dragDropSafetyTxt"
                [selectedFile]="safetyCustomUploadForm.file"
                (fileCleared)="resetSafetyCustomUpload()"
                (fileSelected)="onSafetyCustomFileSelected($event)">
              </zcs-file-upload>

              <!-- Extracted Metadata -->
              <div
                *ngIf="extractedSafetyCustomMetadata"
                class="tw-mt-4 tw-p-4 tw-bg-grey-100 tw-rounded-lg">
                <h4 class="tw-text-base tw-font-semibold tw-text-grey-800 tw-mb-2">{{ labels.extractedMetadata }}</h4>
                <ion-list mode="md" lines="none">
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.safetyCountryCode, value: extractedSafetyCustomMetadata.countryCode }"></ng-container>
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.safetyRegionCode, value: extractedSafetyCustomMetadata.regionCode }"></ng-container>
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.safetyVersion, value: extractedSafetyCustomMetadata.version }"></ng-container>
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.safetyOverrideKey, value: extractedSafetyCustomMetadata.overrideKey }"></ng-container>
                </ion-list>
              </div>

              <!-- Notes -->
              <div class="fx-col fx-gap-1 tw-mt-3">
                <mat-label class="text-grey-400">{{ labels.notesOptional }}</mat-label>
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom w-full"
                  subscriptSizing="dynamic">
                  <textarea
                    matInput
                    rows="2"
                    [placeholder]="labels.anyNotes"
                    [(ngModel)]="safetyCustomUploadForm.notes">
                  </textarea>
                </mat-form-field>
              </div>

              <!-- Progress -->
              <div
                *ngIf="isUploadingSafetyCustom"
                class="tw-mt-3">
                <mat-progress-bar
                  mode="determinate"
                  [value]="safetyCustomUploadProgress">
                </mat-progress-bar>
                <p class="text-grey-400 tw-text-center tw-mt-2">{{ labels.uploading }}: {{ safetyCustomUploadProgress }}%</p>
              </div>

              <!-- Actions -->
              <div class="fx-gap-3 tw-mt-3" style="display: flex; flex-direction: row-reverse;">
                <customer-simple-button
                  [action]="uploadSafetyCustom"
                  [disabled]="!safetyCustomUploadForm.file || !extractedSafetyCustomMetadata || isUploadingSafetyCustom"
                  [label]="labels.uploadSafetyCustomButton">
                </customer-simple-button>
                <customer-simple-button
                  type="warning"
                  [action]="resetSafetyCustomUpload"
                  [disabled]="isUploadingSafetyCustom"
                  [label]="labels.reset">
                </customer-simple-button>
              </div>
            </div>
          </div>

          <!-- Safety Binary Files List -->
          <div
            *ngIf="selectedSafetyDeviceModel"
            class="tw-mt-6">
            <customer-title-content
              class="tw-flex-1 tw-mb-3"
              [content]="labels.yourUploadedSafetyBinaries"
              [title]="labels.safetyBinaryTestFiles">
            </customer-title-content>
            <ng-container *ngIf="(mySafetyBinaries$ | async) as binaries; else loadingBinaries">
              <div class="tw-space-y-4">
                <div
                  *ngFor="let item of binaries"
                  class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
                  <!-- Header -->
                  <div class="tw-bg-yellow-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                    <h5 class="tw-text-yellow-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                      <span class="tw-w-2 tw-h-2 tw-bg-yellow-500 tw-rounded-full tw-mr-3"></span>
                      {{ item.name }}
                    </h5>
                    <p class="tw-text-xs tw-text-gray-500 tw-mt-1">Build: {{ item.metadata?.buildDate }} | {{ (item.size / 1024).toFixed(1) }} KB</p>
                  </div>

                  <!-- Content -->
                  <div class="tw-p-4">
                    <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                      <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ item | json }}</pre>
                    </div>

                    <!-- Copy button -->
                    <div class="tw-mt-3 tw-flex tw-justify-end">
                      <ngx-copy-to-clipboard
                        additionalClasses="tw-text-sm tw-text-yellow-600"
                        copiedTooltip="Copied!"
                        size="sm"
                        tooltip="Copy to clipboard"
                        [textToCopy]="item | json">
                        Copy
                      </ngx-copy-to-clipboard>
                    </div>
                  </div>
                </div>

                <div
                  *ngIf="binaries.length === 0"
                  class="tw-text-center tw-py-8 tw-text-gray-400">
                  <p>{{ labels.noSafetyBinaryFiles }}</p>
                </div>
              </div>
            </ng-container>
            <ng-template #loadingBinaries>
              <div class="tw-flex tw-justify-center tw-py-8">
                <mat-spinner diameter="32"></mat-spinner>
              </div>
            </ng-template>
          </div>

          <!-- Safety Custom Files List -->
          <div
            *ngIf="selectedSafetyDeviceModel"
            class="tw-mt-4">
            <customer-title-content
              class="tw-flex-1 tw-mb-3"
              [content]="labels.yourUploadedSafetyCustom"
              [title]="labels.safetyCustomTestFiles">
            </customer-title-content>
            <ng-container *ngIf="(mySafetyCustom$ | async) as customs; else loadingCustom">
              <div class="tw-space-y-4">
                <div
                  *ngFor="let item of customs"
                  class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
                  <!-- Header -->
                  <div class="tw-bg-yellow-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                    <h5 class="tw-text-yellow-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                      <span class="tw-w-2 tw-h-2 tw-bg-yellow-500 tw-rounded-full tw-mr-3"></span>
                      {{ item.name }}
                    </h5>
                    <p class="tw-text-xs tw-text-gray-500 tw-mt-1">{{ item.metadata?.countryName }} | Region: {{ item.metadata?.regionCode }} | Ver: {{ item.metadata?.version }}</p>
                  </div>

                  <!-- Content -->
                  <div class="tw-p-4">
                    <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                      <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ item | json }}</pre>
                    </div>

                    <!-- Copy button -->
                    <div class="tw-mt-3 tw-flex tw-justify-end">
                      <ngx-copy-to-clipboard
                        additionalClasses="tw-text-sm tw-text-yellow-600"
                        copiedTooltip="Copied!"
                        size="sm"
                        tooltip="Copy to clipboard"
                        [textToCopy]="item | json">
                        Copy
                      </ngx-copy-to-clipboard>
                    </div>
                  </div>
                </div>

                <div
                  *ngIf="customs.length === 0"
                  class="tw-text-center tw-py-8 tw-text-gray-400">
                  <p>{{ labels.noSafetyCustomFiles }}</p>
                </div>
              </div>
            </ng-container>
            <ng-template #loadingCustom>
              <div class="tw-flex tw-justify-center tw-py-8">
                <mat-spinner diameter="32"></mat-spinner>
              </div>
            </ng-template>
          </div>
        </div>

        <!-- Inverter Test Firmware List -->
        <div *ngIf="selectedDeviceType === 'INVERTER'">
          <customer-title-content
            class="tw-flex-1 tw-mt-3 tw-mb-3"
            [content]="labels.yourUploadedTestFirmware"
            [title]="labels.inverterTestFirmware">
          </customer-title-content>

          <!-- Device Model Filter -->
          <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mb-3">
            <mat-label class="text-grey-400">{{ labels.filterByDeviceModel }}</mat-label>
            <div class="fx-row fx-gap-3 fx-wrap">
              <mat-form-field
                class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
                subscriptSizing="dynamic">
                <mat-select [(ngModel)]="firmwareListFilterDeviceModel">
                  <mat-option value="">{{ labels.allDeviceModels }}</mat-option>
                  <mat-option
                    *ngFor="let model of (availableDeviceModels$ | async)"
                    [value]="model.value">
                    {{ model.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <!-- Loading spinner -->
          <div
            *ngIf="isLoadingFirmware"
            class="tw-flex tw-justify-center tw-py-8">
            <mat-spinner diameter="32"></mat-spinner>
          </div>

          <ng-container *ngIf="(myTestFirmware$ | async) as firmware">
            <div
              *ngIf="!isLoadingFirmware"
              class="tw-space-y-4">
              <div
                *ngFor="let fw of filterFirmwareByDeviceModel(firmware, firmwareListFilterDeviceModel)"
                class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-bg-purple-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                  <h5 class="tw-text-purple-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                    <span class="tw-w-2 tw-h-2 tw-bg-purple-500 tw-rounded-full tw-mr-3"></span>
                    {{ fw.metadata.deviceModel }} / {{ fw.metadata.moduleType }} / {{ fw.metadata.chipId }}
                  </h5>
                  <p class="tw-text-xs tw-text-gray-500 tw-mt-1">Version: {{ fw.metadata.version }}</p>
                </div>

                <!-- Content -->
                <div class="tw-p-4">
                  <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                    <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ fw | json }}</pre>
                  </div>

                  <!-- Copy button -->
                  <div class="tw-mt-3 tw-flex tw-justify-end">
                    <ngx-copy-to-clipboard
                      additionalClasses="tw-text-sm tw-text-purple-600"
                      copiedTooltip="Copied!"
                      size="sm"
                      tooltip="Copy to clipboard"
                      [textToCopy]="fw | json">
                      Copy
                    </ngx-copy-to-clipboard>
                  </div>
                </div>
              </div>

              <div
                *ngIf="firmware.length === 0"
                class="tw-text-center tw-py-8 tw-text-gray-400">
                <p>{{ labels.noTestFirmwareUploaded }}</p>
              </div>
              <div
                *ngIf="firmware.length > 0 && filterFirmwareByDeviceModel(firmware, firmwareListFilterDeviceModel).length === 0"
                class="tw-text-center tw-py-8 tw-text-gray-400">
                <p>{{ labels.noFirmwareForFilter }}</p>
              </div>
            </div>
          </ng-container>
        </div>

        <!-- Logger Test Firmware List -->
        <div *ngIf="selectedDeviceType === 'LOGGER'">
          <customer-title-content
            class="tw-flex-1 tw-mt-3 tw-mb-3"
            [content]="labels.yourUploadedLoggerFirmware"
            [title]="labels.loggerTestFirmware">
          </customer-title-content>

          <!-- Loading spinner -->
          <div
            *ngIf="isLoadingLoggerFirmware"
            class="tw-flex tw-justify-center tw-py-8">
            <mat-spinner diameter="32"></mat-spinner>
          </div>

          <ng-container *ngIf="(myLoggerFirmware$ | async) as firmware">
            <div
              *ngIf="!isLoadingLoggerFirmware"
              class="tw-space-y-4">
              <div
                *ngFor="let fw of firmware"
                class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-bg-orange-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                  <h5 class="tw-text-orange-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                    <span class="tw-w-2 tw-h-2 tw-bg-orange-500 tw-rounded-full tw-mr-3"></span>
                    {{ fw.metadata.loggerType }}
                  </h5>
                  <p class="tw-text-xs tw-text-gray-500 tw-mt-1">{{ fw.metadata.originalFilename }}</p>
                </div>

                <!-- Content -->
                <div class="tw-p-4">
                  <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                    <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ fw | json }}</pre>
                  </div>

                  <!-- Copy button -->
                  <div class="tw-mt-3 tw-flex tw-justify-end">
                    <ngx-copy-to-clipboard
                      additionalClasses="tw-text-sm tw-text-orange-600"
                      copiedTooltip="Copied!"
                      size="sm"
                      tooltip="Copy to clipboard"
                      [textToCopy]="fw | json">
                      Copy
                    </ngx-copy-to-clipboard>
                  </div>
                </div>
              </div>

              <div
                *ngIf="firmware.length === 0"
                class="tw-text-center tw-py-8 tw-text-gray-400">
                <p>{{ labels.noLoggerFirmwareUploaded }}</p>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </mat-step>

    <!-- STEP 2: VIEW PRODUCTION CONFIG -->
    <mat-step [label]="labels.viewConfig" [editable]="true">
      <div class="tw-py-4">
        <!-- INVERTER CONFIGURATIONS -->
        <div *ngIf="selectedDeviceType === 'INVERTER'">
          <customer-title-content
            class="tw-flex-1 tw-mb-3"
            [content]="labels.productionConfigurationForInverters"
            [title]="labels.inverterFirmwareConfiguration">
          </customer-title-content>

          <!-- Device Model Filter -->
          <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mb-3">
            <mat-label class="text-grey-400">{{ labels.filterByDeviceModel }}</mat-label>
            <div class="fx-row fx-gap-3 fx-wrap">
              <mat-form-field
                class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
                subscriptSizing="dynamic">
                <mat-select [(ngModel)]="configFilterDeviceModel">
                  <mat-option value="">{{ labels.allDeviceModels }}</mat-option>
                  <mat-option
                    *ngFor="let model of (availableDeviceModels$ | async)"
                    [value]="model.value">
                    {{ model.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div
            *ngIf="(productionConfig$ | async) as config"
            class="tw-space-y-6">
            <ng-container *ngFor="let pattern of config | keyvalue">
              <!-- Only show INVERTER devices with at least one module with data -->
              <div
                *ngIf="shouldShowPatternWithFilter(pattern.value, configFilterDeviceModel)"
                class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-bg-blue-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                  <h5 class="tw-text-blue-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                    <span class="tw-w-2 tw-h-2 tw-bg-blue-500 tw-rounded-full tw-mr-3"></span>
                    {{ pattern.value.ID }} - {{ pattern.key }}
                  </h5>
                </div>

                <!-- Content -->
                <div class="tw-p-4">
                  <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                    <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ pattern.value | json }}</pre>
                  </div>

                  <!-- Copy button -->
                  <div class="tw-mt-3 tw-flex tw-justify-end">
                    <ngx-copy-to-clipboard
                      additionalClasses="tw-text-sm tw-text-blue-600"
                      copiedTooltip="Copied!"
                      size="sm"
                      tooltip="Copy to clipboard"
                      [textToCopy]="pattern.value | json">
                      Copy
                    </ngx-copy-to-clipboard>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- LOGGER CONFIGURATIONS -->
        <div *ngIf="selectedDeviceType === 'LOGGER'">
          <customer-title-content
            class="tw-flex-1 tw-mb-3"
            [content]="labels.productionConfigurationForLoggers"
            [title]="labels.loggerFirmwareConfiguration">
          </customer-title-content>

          <div
            *ngIf="(productionConfig$ | async) as config"
            class="tw-space-y-6">
            <ng-container *ngFor="let pattern of config | keyvalue">
              <!-- Only show LOGGER devices -->
              <div
                *ngIf="isLoggerPattern(pattern.value)"
                class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-bg-green-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                  <h5 class="tw-text-green-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                    <span class="tw-w-2 tw-h-2 tw-bg-green-500 tw-rounded-full tw-mr-3"></span>
                    {{ pattern.value.ID }} - {{ pattern.key }}
                  </h5>
                </div>

                <!-- Content -->
                <div class="tw-p-4">
                  <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                    <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ pattern.value | json }}</pre>
                  </div>

                  <!-- Copy button -->
                  <div class="tw-mt-3 tw-flex tw-justify-end">
                    <ngx-copy-to-clipboard
                      additionalClasses="tw-text-sm tw-text-green-600"
                      copiedTooltip="Copied!"
                      size="sm"
                      tooltip="Copy to clipboard"
                      [textToCopy]="pattern.value | json">
                      Copy
                    </ngx-copy-to-clipboard>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- SAFETY CONFIGURATIONS -->
        <div *ngIf="selectedDeviceType === 'SAFETY'">
          <customer-title-content
            class="tw-flex-1 tw-mb-3"
            [content]="labels.productionConfigurationForSafety"
            [title]="labels.safetyFirmwareConfiguration">
          </customer-title-content>

          <!-- Device Model Filter for Safety -->
          <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mb-3">
            <mat-label class="text-grey-400">{{ labels.filterByDeviceModel }}</mat-label>
            <div class="fx-row fx-gap-3 fx-wrap">
              <mat-form-field
                class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
                subscriptSizing="dynamic">
                <mat-select [(ngModel)]="configFilterDeviceModel">
                  <mat-option value="">{{ labels.allDeviceModels }}</mat-option>
                  <mat-option
                    *ngFor="let model of (availableDeviceModels$ | async)"
                    [value]="model.value">
                    {{ model.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div
            *ngIf="(productionConfig$ | async) as config"
            class="tw-space-y-6">
            <ng-container *ngFor="let pattern of config | keyvalue">
              <!-- Only show INVERTER devices that have SAFETY config -->
              <div
                *ngIf="hasSafetyConfig(pattern.value) && matchesDeviceModelFilter(pattern.key, configFilterDeviceModel)"
                class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-bg-yellow-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                  <h5 class="tw-text-yellow-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                    <span class="tw-w-2 tw-h-2 tw-bg-yellow-500 tw-rounded-full tw-mr-3"></span>
                    {{ pattern.value.ID }} - {{ pattern.key }}
                  </h5>
                </div>

                <!-- Content: Raw JSON -->
                <div class="tw-p-4">
                  <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                    <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ { SAFETY: $any(pattern.value).SAFETY, SAFETY_CUSTOM: $any(pattern.value).SAFETY_CUSTOM } | json }}</pre>
                  </div>

                  <!-- Copy button -->
                  <div class="tw-mt-3 tw-flex tw-justify-end">
                    <ngx-copy-to-clipboard
                      additionalClasses="tw-text-sm tw-text-yellow-600"
                      copiedTooltip="Copied!"
                      size="sm"
                      tooltip="Copy to clipboard"
                      [textToCopy]="{ SAFETY: $any(pattern.value).SAFETY, SAFETY_CUSTOM: $any(pattern.value).SAFETY_CUSTOM } | json">
                      Copy
                    </ngx-copy-to-clipboard>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>
      </div>
    </mat-step>

    <!-- STEP 3: MANAGE OVERRIDES -->
    <mat-step [label]="labels.manageOverrides" [editable]="true">
      <div class="tw-py-4">
        <!-- INVERTER WIZARD -->
        <div *ngIf="selectedDeviceType === 'INVERTER'">
          <customer-title-content
            class="tw-flex-1"
            [title]="labels.createInverterOverride">
          </customer-title-content>

          <!-- Step 1: Select Pattern -->
          <div
            *ngIf="wizardStep === 'select-pattern'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [content]="labels.chooseDevicePattern"
              [title]="labels.step1SelectDevicePattern">
            </customer-title-content>

            <!-- Device Model Filter -->
            <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mb-3">
              <mat-label class="text-grey-400">{{ labels.filterByDeviceModel }}</mat-label>
              <div class="fx-row fx-gap-3 fx-wrap">
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
                  subscriptSizing="dynamic">
                  <mat-select [(ngModel)]="wizardFilterDeviceModel">
                    <mat-option value="">{{ labels.allDeviceModels }}</mat-option>
                    <mat-option
                      *ngFor="let model of (availableDeviceModels$ | async)"
                      [value]="model.value">
                      {{ model.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>
            </div>

            <!-- Loading spinner -->
            <div
              *ngIf="isLoadingConfig"
              class="tw-flex tw-justify-center tw-py-8">
              <mat-spinner diameter="32"></mat-spinner>
            </div>

            <ng-container *ngIf="(availablePatterns$ | async) as patterns">
              <ion-list
                *ngIf="!isLoadingConfig"
                mode="md"
                lines="none">
                <ion-item
                  *ngFor="let item of filterPatternsByDeviceModel(patterns, wizardFilterDeviceModel)"
                  button
                  (click)="selectPattern(item.pattern)">
                  <ion-label class="text-grey-400">
                    <h3>{{ item.id }}</h3>
                    <p>{{ item.pattern }}</p>
                  </ion-label>
                  <ion-icon
                    class="text-grey-400"
                    name="chevron-forward-outline"
                    slot="end">
                  </ion-icon>
                </ion-item>
                <ion-item *ngIf="filterPatternsByDeviceModel(patterns, wizardFilterDeviceModel).length === 0 && patterns.length > 0">
                  <ion-label class="text-grey-400">{{ labels.noFirmwareForFilter }}</ion-label>
                </ion-item>
              </ion-list>
            </ng-container>
          </div>

          <!-- Step 2: Select Module -->
          <div
            *ngIf="wizardStep === 'select-module'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [title]="labels.step2SelectModuleType"
              [content]="'Pattern: ' + wizardSelections.pattern">
            </customer-title-content>
            <ion-list
              mode="md"
              lines="none">
              <ion-item
                *ngFor="let module of (availableModules$ | async)"
                button
                (click)="selectModule(module)">
                <ion-label class="text-grey-400">{{ module }}</ion-label>
                <ion-icon
                  class="text-grey-400"
                  name="chevron-forward-outline"
                  slot="end">
                </ion-icon>
              </ion-item>
            </ion-list>
            <div
              class="fx-gap-3 tw-mt-3"
              style="display: flex; flex-direction: row-reverse;">
              <customer-simple-button
                type="warning"
                [action]="wizardBack"
                [label]="labels.back">
              </customer-simple-button>
            </div>
          </div>

          <!-- Step 3: Select Chip -->
          <div
            *ngIf="wizardStep === 'select-chip'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [title]="labels.step3SelectChip"
              [content]="'Pattern: ' + wizardSelections.pattern + ' | Module: ' + wizardSelections.moduleType">
            </customer-title-content>
            <ion-list
              mode="md"
              lines="none">
              <ion-item
                *ngFor="let chip of (availableChips$ | async)"
                button
                (click)="selectChip(chip)">
                <ion-label class="text-grey-400">
                  <h3>{{ chip.CHIP }} - {{ chip.MODEL }}</h3>
                  <p>{{ labels.currentVersion }}: {{ chip.LATEST_VERSION }}</p>
                </ion-label>
                <ion-icon
                  name="chevron-forward-outline"
                  slot="end">
                </ion-icon>
              </ion-item>
            </ion-list>
            <div
              class="fx-gap-3 tw-mt-3"
              style="display: flex; flex-direction: row-reverse;">
              <customer-simple-button
                type="warning"
                [action]="wizardBack"
                [label]="labels.back">
              </customer-simple-button>
            </div>
          </div>

          <!-- Step 4: Select Firmware -->
          <div
            *ngIf="wizardStep === 'select-firmware'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [title]="labels.step4SelectTestFirmware"
              [content]="'Pattern: ' + wizardSelections.pattern + ' | Module: ' + wizardSelections.moduleType + ' | Chip: ' + wizardSelections.chipId">
            </customer-title-content>

            <!-- Loading spinner -->
            <div
              *ngIf="isLoadingCompatibleFirmware"
              class="tw-flex tw-justify-center tw-py-8">
              <mat-spinner diameter="32"></mat-spinner>
            </div>

            <ng-container *ngIf="(compatibleFirmware$ | async) as firmwareList">
              <ion-list
                *ngIf="!isLoadingCompatibleFirmware"
                mode="md"
                lines="none">
                <ion-item
                  *ngFor="let fw of firmwareList"
                  button
                  (click)="selectFirmware(fw)">
                  <ion-label class="text-grey-400">
                    <h3>{{ fw.metadata.version }}</h3>
                    <p>{{ fw.metadata.notes }}</p>
                    <p>{{ labels.uploaded }}: {{ fw.uploadedAt | date:'short' }}</p>
                  </ion-label>
                  <ion-icon
                    class="text-grey-400"
                    name="chevron-forward-outline"
                    slot="end">
                  </ion-icon>
                </ion-item>
                <ion-item *ngIf="firmwareList.length === 0">
                  <ion-label class="text-grey-400">{{ labels.noCompatibleFirmware }}</ion-label>
                </ion-item>
              </ion-list>
            </ng-container>

            <div
              class="fx-gap-3 tw-mt-3"
              style="display: flex; flex-direction: row-reverse;">
              <customer-simple-button
                type="warning"
                [action]="wizardBack"
                [label]="labels.back">
              </customer-simple-button>
            </div>
          </div>

          <!-- Step 5: Preview and Confirm -->
          <div
            *ngIf="wizardStep === 'preview'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [content]="labels.reviewOverrideConfiguration"
              [title]="labels.step5PreviewAndConfirm">
            </customer-title-content>
            <ion-list
              mode="md"
              lines="none">
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.pattern, value: wizardSelections.pattern }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.module, value: wizardSelections.moduleType }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.chip, value: wizardSelections.chipId }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.originalVersion, value: wizardSelections.originalChipData?.LATEST_VERSION }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.newVersion, value: wizardSelections.selectedFirmware?.metadata?.version }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.firebasePath, value: wizardSelections.selectedFirmware?.path }"></ng-container>
            </ion-list>
            <div
              class="fx-gap-3 tw-mt-3"
              style="display: flex; flex-direction: row-reverse;">
              <customer-simple-button
                [action]="confirmAndSaveOverride"
                [label]="labels.confirmAndSave">
              </customer-simple-button>
              <customer-simple-button
                type="warning"
                [action]="wizardBack"
                [label]="labels.back">
              </customer-simple-button>
            </div>
          </div>
        </div>

        <!-- LOGGER WIZARD -->
        <div *ngIf="selectedDeviceType === 'LOGGER'">
          <customer-title-content
            class="tw-flex-1"
            [title]="labels.createLoggerOverride">
          </customer-title-content>

          <!-- Step 1: Select Pattern -->
          <div
            *ngIf="loggerWizardStep === 'select-pattern'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [content]="labels.chooseLoggerPattern"
              [title]="labels.step1SelectLoggerPattern">
            </customer-title-content>
            <ion-list
              mode="md"
              lines="none">
              <ion-item
                *ngFor="let item of (availableLoggerPatterns$ | async)"
                button
                (click)="selectLoggerPattern(item.pattern)">
                <ion-label class="text-grey-400">
                  <h3>{{ item.id }}</h3>
                  <p>{{ item.pattern }}</p>
                </ion-label>
                <ion-icon
                  class="text-grey-400"
                  name="chevron-forward-outline"
                  slot="end">
                </ion-icon>
              </ion-item>
            </ion-list>
          </div>

          <!-- Step 2: Edit Config -->
          <div
            *ngIf="loggerWizardStep === 'edit-config'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [title]="labels.step2ConfigureLoggerOverride"
              [content]="'Pattern: ' + loggerWizardSelections.pattern + ' | Logger Type: ' + loggerWizardSelections.originalConfig?.DEVICE_MODEL_ID">
            </customer-title-content>

            <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mb-3">
              <mat-label class="text-grey-400">{{ labels.versionFields }}</mat-label>
              <div class="fx-row fx-gap-3 fx-wrap">
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom"
                  subscriptSizing="dynamic">
                  <input
                    matInput
                    [placeholder]="labels.latestSolarmanLoggerByWeb"
                    [(ngModel)]="loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER_BY_WEB">
                </mat-form-field>
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom"
                  subscriptSizing="dynamic">
                  <input
                    matInput
                    [placeholder]="labels.latestSolarmanLogger"
                    [(ngModel)]="loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER">
                </mat-form-field>
              </div>
            </div>

            <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mb-3">
              <mat-label class="text-grey-400">{{ labels.linkSolarmanLogger }} *</mat-label>
              <div class="fx-row fx-gap-3 fx-wrap">
                <mat-form-field
                  class="custom-mat-form-field custom-border-bottom tw-min-w-[300px]"
                  subscriptSizing="dynamic">
                  <mat-select
                    [placeholder]="labels.selectUploadedLoggerFirmware"
                    [(ngModel)]="loggerWizardSelections.override.LINK_SOLARMAN_LOGGER">
                    <mat-option
                      *ngFor="let fw of (myLoggerFirmware$ | async)"
                      [value]="fw.downloadURL">
                      {{ fw.metadata.loggerType }} - {{ fw.metadata.originalFilename }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>
            </div>

            <div
              class="fx-gap-3 tw-mt-3"
              style="display: flex; flex-direction: row-reverse;">
              <customer-simple-button
                [action]="loggerWizardNext"
                [disabled]="!isLoggerFormValid()"
                [label]="labels.next">
              </customer-simple-button>
              <customer-simple-button
                type="warning"
                [action]="loggerWizardBack"
                [label]="labels.back">
              </customer-simple-button>
            </div>
          </div>

          <!-- Step 3: Preview -->
          <div
            *ngIf="loggerWizardStep === 'preview'"
            class="tw-mt-3">
            <customer-title-content
              class="tw-mb-3"
              [content]="labels.reviewLoggerOverrideConfiguration"
              [title]="labels.step3PreviewAndConfirm">
            </customer-title-content>
            <ion-list
              mode="md"
              lines="none">
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.pattern, value: loggerWizardSelections.pattern }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.loggerTypeLabel, value: loggerWizardSelections.originalConfig?.DEVICE_MODEL_ID }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.latestSolarmanLoggerByWeb, value: loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER_BY_WEB }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.latestSolarmanLogger, value: loggerWizardSelections.override.LATEST_SOLARMAN_LOGGER }"></ng-container>
              <ng-container *ngTemplateOutlet="configItem; context: { label: labels.linkSolarmanLogger, value: loggerWizardSelections.override.LINK_SOLARMAN_LOGGER }"></ng-container>
            </ion-list>
            <div
              class="fx-gap-3 tw-mt-3"
              style="display: flex; flex-direction: row-reverse;">
              <customer-simple-button
                [action]="confirmAndSaveLoggerOverride"
                [label]="labels.confirmAndSave">
              </customer-simple-button>
              <customer-simple-button
                type="warning"
                [action]="loggerWizardBack"
                [label]="labels.back">
              </customer-simple-button>
            </div>
          </div>
        </div>

        <!-- Active Inverter Overrides -->
        <div
          *ngIf="selectedDeviceType === 'INVERTER'"
          class="tw-mt-4">
          <customer-title-content
            class="tw-flex-1 tw-mb-3"
            [content]="labels.manageCurrentFirmwareOverrides"
            [title]="labels.myActiveInverterOverrides">
          </customer-title-content>

          <div
            *ngIf="(myOverrides$ | async) as overrides"
            class="tw-space-y-4">
            <div
              *ngFor="let pattern of overrides | keyvalue"
              class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
              <!-- Header -->
              <div class="tw-bg-purple-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                <h5 class="tw-text-indigo-500 tw-uppercase tw-text-sm tw-flex tw-items-center">
                  <span class="tw-w-2 tw-h-2 tw-bg-indigo-500 tw-rounded-full tw-mr-3"></span>
                  Pattern: {{ pattern.key }}
                </h5>
              </div>

              <!-- Content -->
              <div class="tw-p-4">
                <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                  <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ pattern.value | json }}</pre>
                </div>

                <!-- Actions -->
                <div class="tw-mt-3 tw-flex tw-justify-end tw-gap-3">
                  <ngx-copy-to-clipboard
                    additionalClasses="tw-text-sm tw-text-indigo-500"
                    copiedTooltip="Copied!"
                    size="sm"
                    tooltip="Copy to clipboard"
                    [textToCopy]="pattern.value | json">
                    Copy
                  </ngx-copy-to-clipboard>
                  <button
                    type="button"
                    class="tw-text-sm tw-text-red-600 hover:tw-text-red-800 tw-flex tw-items-center tw-gap-1"
                    (click)="removeOverridePattern(pattern.key)">
                    <mat-icon class="tw-text-base">delete</mat-icon>
                    Delete Pattern
                  </button>
                </div>
              </div>
            </div>

            <div
              *ngIf="(overrides | keyvalue).length === 0"
              class="tw-text-center tw-py-8 tw-text-gray-400">
              <p>{{ labels.noActiveInverterOverrides }}</p>
            </div>

            <div
              *ngIf="(overrides | keyvalue).length > 0"
              class="tw-flex tw-justify-end tw-mt-4">
              <customer-simple-button
                [action]="clearAllOverrides"
                [label]="labels.clearAllInverterOverrides">
              </customer-simple-button>
            </div>
          </div>
        </div>

        <!-- Active Logger Overrides -->
        <div
          *ngIf="selectedDeviceType === 'LOGGER'"
          class="tw-mt-4">
          <customer-title-content
            class="tw-flex-1 tw-mb-3"
            [content]="labels.manageCurrentLoggerOverrides"
            [title]="labels.myActiveLoggerOverrides">
          </customer-title-content>

          <div
            *ngIf="(myLoggerOverrides$ | async) as overrides"
            class="tw-space-y-4">
            <div
              *ngFor="let pattern of overrides | keyvalue"
              class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
              <!-- Header -->
              <div class="tw-bg-ottanio-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                <h5 class="tw-text-ottanio-500 tw-uppercase tw-text-sm tw-flex tw-items-center">
                  <span class="tw-w-2 tw-h-2 tw-bg-ottanio-500 tw-rounded-full tw-mr-3"></span>
                  Pattern: {{ pattern.key }}
                </h5>
                <p class="tw-text-xs tw-text-gray-500 tw-mt-1">Logger Type: {{ pattern.value.DEVICE_MODEL_ID }}</p>
              </div>

              <!-- Content -->
              <div class="tw-p-4">
                <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                  <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ pattern.value | json }}</pre>
                </div>

                <!-- Actions -->
                <div class="tw-mt-3 tw-flex tw-justify-end tw-gap-3">
                  <ngx-copy-to-clipboard
                    additionalClasses="tw-text-sm tw-text-ottanio-500"
                    copiedTooltip="Copied!"
                    size="sm"
                    tooltip="Copy to clipboard"
                    [textToCopy]="pattern.value | json">
                    Copy
                  </ngx-copy-to-clipboard>
                  <button
                    type="button"
                    class="tw-text-sm tw-text-red-600 hover:tw-text-red-800 tw-flex tw-items-center tw-gap-1"
                    (click)="removeLoggerOverride(pattern.key)">
                    <mat-icon class="tw-text-base">delete</mat-icon>
                    Delete
                  </button>
                </div>
              </div>
            </div>

            <div
              *ngIf="(overrides | keyvalue).length === 0"
              class="tw-text-center tw-py-8 tw-text-gray-400">
              <p>{{ labels.noActiveLoggerOverrides }}</p>
            </div>
          </div>
        </div>

        <!-- SAFETY WIZARD -->
        <div *ngIf="selectedDeviceType === 'SAFETY'">
          <customer-title-content
            class="tw-flex-1"
            [content]="labels.manageSafetyOverridesDescription"
            [title]="labels.manageSafetyOverrides">
          </customer-title-content>

          <!-- Device Model Selection (required for SAFETY wizard) -->
          <div class="fx-col fx-gap-1 p-4 border-grey-200 border-1 radius-4 tw-mt-3 tw-mb-4">
            <mat-label class="text-grey-400">{{ labels.deviceModel }} *</mat-label>
            <mat-form-field
              class="custom-mat-form-field custom-border-bottom tw-min-w-[200px]"
              subscriptSizing="dynamic">
              <mat-select
                [(ngModel)]="selectedSafetyDeviceModel"
                [placeholder]="labels.selectDeviceModel"
                (selectionChange)="onSafetyDeviceModelChange()">
                <mat-option
                  *ngFor="let model of (availableDeviceModels$ | async)"
                  [value]="model.value">
                  {{ model.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div *ngIf="selectedSafetyDeviceModel">
            <!-- Step 1: Select Files -->
            <div
              *ngIf="safetyWizardStep === 'select-files'"
              class="tw-mt-3">
              <customer-title-content
                class="tw-mb-3"
                [title]="'Step 1: ' + labels.selectFiles">
              </customer-title-content>

              <!-- Binary Selection -->
              <div class="tw-mb-4">
                <h4 class="tw-text-sm tw-font-semibold tw-text-grey-700 tw-mb-2">{{ labels.selectSafetyBinary }} ({{ labels.optional }})</h4>
                <ng-container *ngIf="(mySafetyBinaries$ | async) as binaries; else loadingBinaries2">
                  <ion-list mode="md" lines="none">
                    <ion-item
                      *ngFor="let item of binaries"
                      button
                      [class.tw-bg-green-50]="safetyWizardSelections.selectedBinaryPath === item.path"
                      (click)="safetyWizardSelections.selectedBinaryPath = item.path">
                      <ion-label>
                        <h3>{{ item.name }}</h3>
                        <p>Build: {{ item.metadata?.buildDate }}</p>
                      </ion-label>
                      <ion-icon
                        *ngIf="safetyWizardSelections.selectedBinaryPath === item.path"
                        class="tw-text-green-600"
                        name="checkmark-circle"
                        slot="end">
                      </ion-icon>
                    </ion-item>
                    <ion-item
                      *ngIf="binaries.length === 0"
                      lines="none">
                      <ion-label class="text-grey-400">{{ labels.noSafetyBinaryFiles }}</ion-label>
                    </ion-item>
                  </ion-list>
                </ng-container>
                <ng-template #loadingBinaries2>
                  <div class="tw-flex tw-justify-center tw-p-4">
                    <mat-spinner diameter="24"></mat-spinner>
                  </div>
                </ng-template>
              </div>

              <!-- Custom Files Selection (multi-select) -->
              <div>
                <h4 class="tw-text-sm tw-font-semibold tw-text-grey-700 tw-mb-2">{{ labels.selectSafetyCustomFiles }} ({{ labels.optional }})</h4>
                <ng-container *ngIf="(mySafetyCustom$ | async) as customs; else loadingCustom2">
                  <ion-list mode="md" lines="none">
                    <ion-item
                      *ngFor="let item of customs"
                      button
                      [class.tw-bg-green-50]="safetyWizardSelections.selectedCustomPaths.includes(item.path)"
                      (click)="toggleSafetyCustomPath(item.path)">
                      <ion-label>
                        <h3>{{ item.name }}</h3>
                        <p>{{ item.metadata?.countryName }} | Region: {{ item.metadata?.regionCode }} | Ver: {{ item.metadata?.version }}</p>
                      </ion-label>
                      <ion-icon
                        *ngIf="safetyWizardSelections.selectedCustomPaths.includes(item.path)"
                        class="tw-text-green-600"
                        name="checkmark-circle"
                        slot="end">
                      </ion-icon>
                    </ion-item>
                    <ion-item
                      *ngIf="customs.length === 0"
                      lines="none">
                      <ion-label class="text-grey-400">{{ labels.noSafetyCustomFiles }}</ion-label>
                    </ion-item>
                  </ion-list>
                </ng-container>
                <ng-template #loadingCustom2>
                  <div class="tw-flex tw-justify-center tw-p-4">
                    <mat-spinner diameter="24"></mat-spinner>
                  </div>
                </ng-template>
              </div>

              <div
                class="fx-gap-3 tw-mt-4"
                style="display: flex; flex-direction: row-reverse;">
                <customer-simple-button
                  [action]="goToSafetyPreview"
                  [label]="labels.next">
                </customer-simple-button>
              </div>
            </div>

            <!-- Step 2: Preview -->
            <div
              *ngIf="safetyWizardStep === 'preview'"
              class="tw-mt-3">
              <customer-title-content
                class="tw-mb-3"
                [title]="'Step 2: ' + labels.step5PreviewAndConfirm">
              </customer-title-content>

              <ion-list mode="md" lines="none">
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.pattern, value: safetyWizardSelections.pattern }"></ng-container>
                <ng-container *ngTemplateOutlet="configItem; context: { label: labels.deviceModel, value: selectedSafetyDeviceModel }"></ng-container>
                <ng-container *ngIf="safetyWizardSelections.selectedBinaryPath">
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.binaryOverride, value: getSelectedSafetyBinaryDetails()?.name || safetyWizardSelections.selectedBinaryPath }"></ng-container>
                </ng-container>
                <ng-container *ngIf="safetyWizardSelections.selectedCustomPaths.length > 0">
                  <ng-container *ngTemplateOutlet="configItem; context: { label: labels.customOverrides, value: safetyWizardSelections.selectedCustomPaths.length + ' files selected' }"></ng-container>
                </ng-container>
              </ion-list>

              <div
                class="fx-gap-3 tw-mt-4"
                style="display: flex; flex-direction: row-reverse;">
                <customer-simple-button
                  [action]="saveSafetyOverride"
                  [label]="labels.confirmAndSave">
                </customer-simple-button>
                <customer-simple-button
                  type="warning"
                  [action]="resetSafetyWizard"
                  [label]="labels.back">
                </customer-simple-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Active Safety Overrides -->
        <div
          *ngIf="selectedDeviceType === 'SAFETY'"
          class="tw-mt-4">
          <customer-title-content
            class="tw-flex-1 tw-mb-3"
            [content]="labels.manageCurrentSafetyOverrides"
            [title]="labels.mySafetyOverrides">
          </customer-title-content>

          <ng-container *ngIf="(mySafetyOverrides$ | async) as overrides; else loadingOverrides">
          <div class="tw-space-y-4">
            <div
              *ngFor="let pattern of overrides | keyvalue"
              class="tw-bg-white tw-border-grey-200 tw-border tw-rounded-lg tw-overflow-hidden">
              <!-- Header -->
              <div class="tw-bg-yellow-100 tw-px-4 tw-py-3 tw-border-b tw-border-grey-100">
                <h5 class="tw-text-yellow-600 tw-uppercase tw-text-sm tw-flex tw-items-center">
                  <span class="tw-w-2 tw-h-2 tw-bg-yellow-500 tw-rounded-full tw-mr-3"></span>
                  Pattern: {{ pattern.key }}
                </h5>
              </div>

              <!-- Content -->
              <div class="tw-p-4">
                <div class="tw-max-h-96 tw-overflow-y-auto tw-border tw-rounded tw-border-grey-100">
                  <pre class="tw-p-4 tw-text-xs tw-text-gray-400 tw-font-mono tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">{{ pattern.value | json }}</pre>
                </div>

                <!-- Actions -->
                <div class="tw-mt-3 tw-flex tw-justify-end tw-gap-3">
                  <ngx-copy-to-clipboard
                    additionalClasses="tw-text-sm tw-text-yellow-600"
                    copiedTooltip="Copied!"
                    size="sm"
                    tooltip="Copy to clipboard"
                    [textToCopy]="pattern.value | json">
                    Copy
                  </ngx-copy-to-clipboard>
                  <button
                    type="button"
                    class="tw-text-sm tw-text-red-600 hover:tw-text-red-800 tw-flex tw-items-center tw-gap-1"
                    (click)="removeSafetyOverride(pattern.key)">
                    <mat-icon class="tw-text-base">delete</mat-icon>
                    {{ labels.delete }}
                  </button>
                </div>
              </div>
            </div>

            <div
              *ngIf="(overrides | keyvalue).length === 0"
              class="tw-text-center tw-py-8 tw-text-gray-400">
              <p>{{ labels.noActiveSafetyOverrides }}</p>
            </div>
          </div>
          </ng-container>
          <ng-template #loadingOverrides>
            <div class="tw-flex tw-justify-center tw-py-8">
              <mat-spinner diameter="32"></mat-spinner>
            </div>
          </ng-template>
        </div>
      </div>
    </mat-step>
  </mat-stepper>
</div>
